import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { inr, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ShoppingCart, Loader2, Box, Smartphone } from "lucide-react";
import useRazorpay from "@/hooks/useRazorpay";
import { logTermsCheckpoint } from "@/lib/termsCheckpoint";

export default function Cart() {
  const { items, remove, total, count, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [rzpLoading, setRzpLoading] = useState(false);
  const { openCheckout } = useRazorpay();

  /** Razorpay UPI/card/netbanking/wallet path — modal-based, stays on our origin. */
  const checkoutRazorpay = async () => {
    if (!user) return nav("/login", { state: { from: "/cart" } });
    setRzpLoading(true);
    try {
      const { data: order } = await api.post("/payments/razorpay/create-order", {
        listing_ids: items.map((i) => i.id),
      });
      const success = await openCheckout({
        order,
        name: "Jeweltix",
        description: `${count} design${count > 1 ? "s" : ""} · ₹${(order.amount / 100).toFixed(0)}`,
      });
      // Verify signature server-side and fulfil the orders.
      const { data: verified } = await api.post("/payments/razorpay/verify", success);
      if (verified?.success) {
        // Immutable "purchase" checkpoint acceptance — buyer confirms Terms
        // + licence restrictions at the moment of payment.
        logTermsCheckpoint({
          role: "buyer",
          event: "purchase",
          contextId: verified.our_tx_id,
          checkboxes: ["buyer_terms", "privacy"],
        });
        clear();
        toast.success("Payment successful — designs are ready in your dashboard.");
        nav(`/payment/success?tx=${verified.our_tx_id}`);
      }
    } catch (err) {
      // Modal-dismiss + payment.failed both come through as rejected promises
      if (err?.code === "cancelled") toast.info("Payment cancelled — no charge was made.");
      else if (err?.code === "failed") toast.error(err.message || "Payment failed. Please try again.");
      else toast.error(apiErr(err) || err?.message || "Payment could not be completed");
    } finally {
      setRzpLoading(false);
    }
  };

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display font-extrabold text-3xl mb-6 flex items-center gap-3" data-testid="cart-heading">
        <ShoppingCart className="h-7 w-7 text-primary" /> Your cart
        <span className="text-base font-mono text-muted-foreground">({count})</span>
      </h1>

      {count === 0 ? (
        <div className="border border-dashed border-border rounded-md py-24 text-center text-muted-foreground" data-testid="cart-empty">
          Your cart is empty.
          <div className="mt-4">
            <Link to="/marketplace" className="text-primary font-semibold hover:underline">Browse the marketplace →</Link>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="border border-border rounded-md bg-card divide-y divide-border">
            {items.map((it) => (
              <div key={it.id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${it.id}`}>
                <div className="h-14 w-14 rounded bg-slate-900 mesh-grid flex items-center justify-center shrink-0">
                  <Box className="h-6 w-6 text-primary/70" strokeWidth={1.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/design/${it.id}`} className="font-semibold text-sm hover:text-primary line-clamp-1">{it.title}</Link>
                  <p className="text-xs text-muted-foreground font-mono uppercase">{it.file_type} · {it.seller}</p>
                </div>
                <span className="font-display font-bold">{inr(it.price)}</span>
                <button onClick={() => remove(it.id)} data-testid={`cart-remove-${it.id}`} className="text-red-500 hover:text-red-600 p-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="border border-border rounded-md bg-card p-5 h-fit">
            <p className="font-display font-semibold text-lg mb-4">Order summary</p>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Items</span>
              <span className="font-mono">{count}</span>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3 mt-3">
              <span className="font-semibold">Total</span>
              <span className="font-display font-extrabold text-xl" data-testid="cart-total">{inr(total)}</span>
            </div>

            {/* --- Razorpay payment (sole gateway) --- */}
            <div className="mt-5 space-y-2">
              <p className="text-[11px] text-muted-foreground leading-snug border-l-2 border-primary/40 pl-2" data-testid="cart-purchase-consent">
                By completing this purchase you accept the <Link to="/terms/buyer" className="text-primary underline">Buyer Terms</Link> and the licence restrictions on these files (single-buyer use only — no resale or redistribution).
              </p>
              <button
                onClick={checkoutRazorpay}
                disabled={rzpLoading}
                data-testid="cart-checkout-razorpay"
                className="w-full flex items-center justify-center gap-2 rounded-md py-3 font-semibold text-white transition-transform hover:-translate-y-[1px] disabled:opacity-60"
                style={{ background: "linear-gradient(90deg, #072654 0%, #0b3fa8 100%)" }}
              >
                {rzpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
                {rzpLoading ? "Opening Razorpay…" : "Pay with Razorpay"}
              </button>
              <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest text-center">
                UPI · Cards · Netbanking · Wallets
              </p>
            </div>

            <p className="text-[11px] text-muted-foreground mt-4 text-center">
              One payment for all files · escrow-protected
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
